import type { RouteName } from '@/lib/routes';

/**
 * Data model for the "rii.link vs {competitor}" dossier pages. One data file
 * per competitor (see ./data/bitly.ts) feeds the shared CompareDossier — the
 * dub / rebrandly / short-io / tinyurl siblings are data-only forks of the
 * same layout (design-handoff contract).
 *
 * Honesty rails (binding — the page lives or dies on them):
 * - Every competitor cell is sourced: bracketed refs `[1]–[5]` in cell notes
 *   map to `sources`; figures carry the `verified` date and must be
 *   re-verified before each deploy (bump the chip date).
 * - No winner declared in the verdict; symmetric treatment both columns.
 * - Concessions in full — never soften, never pad with asterisks.
 * - The hero chips (claims / sources / concessions counts) are DERIVED from
 *   this data (see `chipCounts`), so they cannot drift from the table.
 */

export type MarkTone = 'green' | 'dim' | 'ink' | 'amber';

/** One table cell: `✓` / `—` / short ALL-CAPS mono value, plus a dim note. */
export type Mark = { m: string; tone: MarkTone; note: string };

export const yes = (note: string): Mark => ({ m: '✓', tone: 'green', note });
export const no = (note: string): Mark => ({ m: '—', tone: 'dim', note });
export const txt = (m: string, note: string, tone: MarkTone = 'ink'): Mark => ({
  m,
  tone,
  note,
});

export type CompareRow = {
  /** Feature name. */
  f: string;
  /** Mandatory one-line "why it matters" rationale under the feature name. */
  why: string;
  /** rii.link column. */
  a: Mark;
  /** Competitor column. */
  b: Mark;
};

export type CompareCategory = { label: string; rows: CompareRow[] };

export type Faq = {
  q: string;
  a: string;
  /** Brand-bold closing recommendation — last item only. */
  pick?: string;
};

export type Source = { n: number; label: string; note: string };

export type HubEntry = {
  name: string;
  route: RouteName;
  /** Mono path label shown on the card, e.g. "/compare/dub". */
  path: string;
  /** Unbuilt siblings render as non-clickable "IN WRITING" cards. */
  live: boolean;
};

export type WhyCard = { title: string; body: string; readout: string };

export type StepCard = { step: string; title: string; body: string; readout: string };

export type Concession = { title: string; body: string };

export type LedgerRow = { tier: string; math: string; value: string; unit: string };

export type CompareDossierData = {
  /** Competitor display name, e.g. "Bitly". */
  competitor: string;
  slug: string;
  /** Human date the competitor data was last checked, e.g. "AUG 2026". */
  verified: string;
  /** Full "at the time of writing" date for the appendix disclaimer. */
  writtenOn: string;

  hero: {
    eyebrow: string;
    titleLine1: string;
    titleLine2: string;
    lead: string;
  };

  /** §01 — 4 decisive bullets each, no winner declared. */
  verdict: {
    title: string;
    lead: string;
    rii: string[];
    them: string[];
    caption: string;
  };

  /** §02 — three differences that survive scrutiny. */
  why: { title: string; cards: WhyCard[] };

  /** §03 — the table. */
  table: {
    title: string;
    lead: string;
    /** Column-head qualifier over the competitor column, e.g. "PUBLIC DOCS". */
    columnNote: string;
    cats: CompareCategory[];
    footnote: string;
  };

  /** §04 — the "to be fair" aside beside the two unfurls (honesty rail:
   *  concede the competitor's domain story in the competitor's terms). */
  fairNote: string;

  /** §05 — cost of scale. */
  cost: {
    title: string;
    lead: string;
    ledgerPill: string;
    ledger: LedgerRow[];
    ledgerFoot: string;
    ledgerStamp: string;
    riiPill: string;
    riiTitle: string;
    riiBody: string;
    riiFoot: string;
    riiStamp: string;
  };

  /** §06 — migration. */
  migration: {
    title: string;
    steps: StepCard[];
    friction: { label: string; pill: string; body: string };
  };

  /** §07 — where the competitor wins (ink band). */
  concessions: {
    title: string;
    lead: string;
    items: Concession[];
    caption: string;
  };

  /** §08 — switching FAQ. */
  faqs: Faq[];

  /** §09 — sibling dossiers. */
  hub: HubEntry[];

  /** Closing CTA panel (competitor-specific reassurance + link). */
  closing: {
    eyebrow: string;
    title: string;
    body: string;
    /** Underlined link under the buttons. */
    linkLabel: string;
    /** App-relative href for that link, e.g. `/en/learn/migrating-50k-links`. */
    linkHref: string;
  };

  /** Appendix. */
  sources: Source[];
  disclaimer: {
    /** Rendered before the correction invite; keep the trademark line. */
    body: string;
  };
};

/** Hero chip counts, derived so they can never drift from the table. */
export function chipCounts(data: CompareDossierData) {
  return {
    claims: data.table.cats.reduce((n, c) => n + c.rows.length, 0),
    sources: data.sources.length,
    concessions: data.concessions.items.length,
  };
}
